import { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { Icon } from '@iconify/react'
import { get, patch, deleteAPI, deleteWithBody } from '../../../../utils/Api'
import { useGlobalContext } from '../../../../utils/Context'
import FormLoader from '../../../../components/loader/FormLoader'
import Pagination from '../../../../components/pagination/Pagination'
import './KnowledgeBase.scss'

type Category =
  | 'rates'
  | 'prohibited_items'
  | 'customs'
  | 'shipping_process'
  | 'payment'
  | 'account'
  | 'tracking'
  | 'insurance'
  | 'packaging'
  | 'zones'
  | 'general'

interface KBEntry {
  _id: string
  category: Category
  question: string
  shortAnswer?: string
  isActive: boolean
  priority: number
  metadata: {
    viewCount: number
    helpfulCount: number
    notHelpfulCount: number
  }
  createdAt: string
}

interface Stats {
  totals: {
    total: number
    active: number
    inactive: number
    totalViews: number
    totalHelpful: number
  }
  byCategory: { _id: string; total: number; active: number; inactive: number }[]
}

const CATEGORY_LABELS: Record<Category, string> = {
  rates: 'Rates & Pricing',
  prohibited_items: 'Prohibited Items',
  customs: 'Customs & Duties',
  shipping_process: 'Shipping Process',
  payment: 'Payments',
  account: 'Account',
  tracking: 'Tracking',
  insurance: 'Insurance',
  packaging: 'Packaging',
  zones: 'Shipping Zones',
  general: 'General',
}

const ALL_CATEGORIES = Object.keys(CATEGORY_LABELS) as Category[]

function RowMenu({
  entry,
  onToggle,
  onDelete,
  onEdit,
}: {
  entry: KBEntry
  onToggle: () => void
  onDelete: () => void
  onEdit: () => void
}) {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [])

  return (
    <div className='row-actions' ref={ref} onClick={(e) => e.stopPropagation()}>
      <button className='kebab-btn' onClick={() => setOpen((v) => !v)}>
        <Icon icon='mdi:dots-vertical' />
      </button>
      {open && (
        <div className='dropdown'>
          <button
            onClick={() => {
              onEdit()
              setOpen(false)
            }}
          >
            <Icon icon='mdi:pencil-outline' /> Edit
          </button>
          <button
            onClick={() => {
              onToggle()
              setOpen(false)
            }}
          >
            <Icon
              icon={entry.isActive ? 'mdi:eye-off-outline' : 'mdi:eye-outline'}
            />
            {entry.isActive ? 'Deactivate' : 'Activate'}
          </button>
          <button
            className='delete'
            onClick={() => {
              onDelete()
              setOpen(false)
            }}
          >
            <Icon icon='mdi:delete-outline' /> Delete
          </button>
        </div>
      )}
    </div>
  )
}

function KnowledgeBase() {
  const navigate = useNavigate()
  const { notify } = useGlobalContext()

  const [stats, setStats] = useState<Stats | null>(null)
  const [entries, setEntries] = useState<KBEntry[]>([])
  const [loading, setLoading] = useState(true)
  const [statsLoading, setStatsLoading] = useState(true)
  const [page, setPage] = useState(1)
  const [totalDocs, setTotalDocs] = useState(0)

  const [filters, setFilters] = useState({
    category: '',
    isActive: '',
    search: '',
  })
  const [searchInput, setSearchInput] = useState('')
  const [selected, setSelected] = useState<string[]>([])
  const searchTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  function fetchStats() {
    setStatsLoading(true)
    get('admin/knowledge-base/stats')
      .then((res: any) => {
        if (res.success) setStats(res.data)
        setStatsLoading(false)
      })
      .catch(() => setStatsLoading(false))
  }

  function fetchEntries(pageNum: number, overrideFilters?: typeof filters) {
    setLoading(true)
    const f = overrideFilters ?? filters
    let url = `admin/knowledge-base?page=${pageNum}&limit=20&sortBy=priority&sortOrder=desc`
    if (f.category) url += `&category=${f.category}`
    if (f.isActive !== '') url += `&isActive=${f.isActive}`
    if (f.search) url += `&search=${encodeURIComponent(f.search)}`

    get(url)
      .then((res: any) => {
        if (res.success) {
          setEntries(res.data.entries || [])
          setTotalDocs(res.data.pagination?.totalPages || 0)
        } else {
          notify('error', res.message || 'Failed to load entries')
        }
        setLoading(false)
      })
      .catch(() => {
        setLoading(false)
        notify('error', 'Failed to load entries')
      })
  }

  useEffect(() => {
    fetchStats()
    fetchEntries(1)
  }, [])

  const handleFilterChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newFilters = { ...filters, [e.target.name]: e.target.value }
    setFilters(newFilters)
    setPage(1)
    setSelected([])
    fetchEntries(1, newFilters)
  }

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value
    setSearchInput(val)
    if (searchTimer.current) clearTimeout(searchTimer.current)
    searchTimer.current = setTimeout(() => {
      const newFilters = { ...filters, search: val }
      setFilters(newFilters)
      setPage(1)
      setSelected([])
      fetchEntries(1, newFilters)
    }, 400)
  }

  const handlePageChange = (newPage: number) => {
    setPage(newPage)
    setSelected([])
    fetchEntries(newPage)
  }

  // Selection
  const toggleSelect = (id: string) =>
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    )

  const toggleSelectAll = () =>
    setSelected(
      selected.length === entries.length ? [] : entries.map((e) => e._id),
    )

  // Row actions
  const toggleEntry = async (entry: KBEntry) => {
    try {
      const res = await patch(`admin/knowledge-base/${entry._id}/toggle`, {})
      if (res.success) {
        notify(
          'success',
          entry.isActive ? 'Entry deactivated' : 'Entry activated',
        )
        setEntries((prev) =>
          prev.map((e) =>
            e._id === entry._id ? { ...e, isActive: !e.isActive } : e,
          ),
        )
        fetchStats()
      } else {
        notify('error', res.message || 'Failed to toggle')
      }
    } catch {
      notify('error', 'Something went wrong')
    }
  }

  const deleteEntry = async (id: string) => {
    if (!window.confirm('Delete this entry? This cannot be undone.')) return
    try {
      const res = await deleteAPI(`admin/knowledge-base/${id}`)
      if (res.success) {
        notify('success', 'Entry deleted')
        setEntries((prev) => prev.filter((e) => e._id !== id))
        fetchStats()
      } else {
        notify('error', res.message || 'Failed to delete')
      }
    } catch {
      notify('error', 'Something went wrong')
    }
  }

  // Bulk actions
  const bulkToggle = async (isActive: boolean) => {
    if (selected.length === 0) return
    try {
      const res = await patch('admin/knowledge-base/bulk/toggle', {
        ids: selected,
        isActive,
      })
      if (res.success) {
        notify(
          'success',
          `${selected.length} entries ${isActive ? 'activated' : 'deactivated'}`,
        )
        setSelected([])
        fetchEntries(page)
        fetchStats()
      } else {
        notify('error', res.message || 'Failed')
      }
    } catch {
      notify('error', 'Something went wrong')
    }
  }

  const bulkDelete = async () => {
    if (selected.length === 0) return
    if (
      !window.confirm(
        `Delete ${selected.length} entries? This cannot be undone.`,
      )
    )
      return
    try {
      const res = await deleteWithBody('admin/knowledge-base/bulk', {
        ids: selected,
      })
      if (res.success) {
        notify('success', `${selected.length} entries deleted`)
        setSelected([])
        fetchEntries(page)
        fetchStats()
      } else {
        notify('error', res.message || 'Failed')
      }
    } catch {
      notify('error', 'Something went wrong')
    }
  }

  const helpfulScore = (entry: KBEntry) => {
    const total = entry.metadata.helpfulCount + entry.metadata.notHelpfulCount
    if (total === 0) return '—'
    return `${Math.round((entry.metadata.helpfulCount / total) * 100)}%`
  }

  return (
    <div className='kb-page'>
      {/* Header */}
      <div className='kb-header'>
        <h2></h2>
        <button
          className='btn-add'
          onClick={() => navigate('/admin/knowledge-base-entry/new')}
        >
          <Icon icon='mdi:plus' /> Add Entry
        </button>
      </div>

      {/* Stats */}
      <div className='kb-stats'>
        <div className='stat-card total'>
          <p>📚 Total</p>
          <h3>{statsLoading ? '…' : (stats?.totals.total ?? 0)}</h3>
        </div>
        <div className='stat-card active'>
          <p>✅ Active</p>
          <h3>{statsLoading ? '…' : (stats?.totals.active ?? 0)}</h3>
        </div>
        <div className='stat-card inactive'>
          <p>🚫 Inactive</p>
          <h3>{statsLoading ? '…' : (stats?.totals.inactive ?? 0)}</h3>
        </div>
        <div className='stat-card views'>
          <p>👁 Views</p>
          <h3>
            {statsLoading
              ? '…'
              : (stats?.totals.totalViews ?? 0).toLocaleString()}
          </h3>
        </div>
        <div className='stat-card helpful'>
          <p>👍 Helpful</p>
          <h3>
            {statsLoading
              ? '…'
              : (stats?.totals.totalHelpful ?? 0).toLocaleString()}
          </h3>
        </div>
      </div>

      {/* Filters */}
      <div className='kb-filters'>
        <select
          name='category'
          value={filters.category}
          onChange={handleFilterChange}
        >
          <option value=''>All Categories</option>
          {ALL_CATEGORIES.map((cat) => (
            <option key={cat} value={cat}>
              {CATEGORY_LABELS[cat]}
            </option>
          ))}
        </select>
        <select
          name='isActive'
          value={filters.isActive}
          onChange={handleFilterChange}
        >
          <option value=''>All Status</option>
          <option value='true'>Active</option>
          <option value='false'>Inactive</option>
        </select>
        <div className='search-wrap'>
          <input
            type='search'
            placeholder='Search entries…'
            value={searchInput}
            onChange={handleSearchChange}
          />
          <button onClick={() => fetchEntries(1)}>Search</button>
        </div>
      </div>

      {/* Bulk action bar */}
      {selected.length > 0 && (
        <div className='bulk-bar'>
          <span>{selected.length} selected</span>
          <button className='btn-activate' onClick={() => bulkToggle(true)}>
            Activate
          </button>
          <button className='btn-deactivate' onClick={() => bulkToggle(false)}>
            Deactivate
          </button>
          <button className='btn-delete' onClick={bulkDelete}>
            Delete
          </button>
        </div>
      )}

      {/* Table */}
      {loading ? (
        <FormLoader position='center' />
      ) : entries.length === 0 ? (
        <div className='kb-empty'>
          <Icon icon='mdi:book-open-blank-variant-outline' />
          <p>No knowledge base entries found</p>
        </div>
      ) : (
        <>
          <div className='kb-table-wrap desktop-table'>
            <table>
              <thead>
                <tr>
                  <th>
                    <input
                      type='checkbox'
                      checked={
                        selected.length === entries.length && entries.length > 0
                      }
                      onChange={toggleSelectAll}
                    />
                  </th>
                  <th>Question</th>
                  <th>Category</th>
                  <th>Status</th>
                  <th>Priority</th>
                  <th>Views</th>
                  <th>👍 Score</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {entries.map((entry) => (
                  <tr
                    key={entry._id}
                    onClick={() =>
                      navigate(`/admin/knowledge-base-entry/${entry._id}`)
                    }
                    style={{ cursor: 'pointer' }}
                  >
                    <td onClick={(e) => e.stopPropagation()}>
                      <input
                        type='checkbox'
                        checked={selected.includes(entry._id)}
                        onChange={() => toggleSelect(entry._id)}
                      />
                    </td>
                    <td style={{ maxWidth: '300px' }}>
                      <span style={{ color: '#446ce4', cursor: 'pointer' }}>
                        {entry.question.length > 60
                          ? entry.question.slice(0, 60) + '…'
                          : entry.question}
                      </span>
                    </td>
                    <td>
                      <span className={` ${entry.category}`}>
                        {CATEGORY_LABELS[entry.category] || entry.category}
                      </span>
                    </td>
                    <td>
                      <span
                        className={`kb-badge ${entry.isActive ? 'active' : 'inactive'}`}
                      >
                        {entry.isActive ? '✅ Active' : '🚫 Inactive'}
                      </span>
                    </td>
                    <td>{entry.priority}</td>
                    <td>{(entry.metadata?.viewCount ?? 0).toLocaleString()}</td>
                    <td>{helpfulScore(entry)}</td>
                    <td onClick={(e) => e.stopPropagation()}>
                      <RowMenu
                        entry={entry}
                        onEdit={() =>
                          navigate(`/admin/knowledge-base-entry/${entry._id}`)
                        }
                        onToggle={() => toggleEntry(entry)}
                        onDelete={() => deleteEntry(entry._id)}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile cards */}
          <div className='kb-mobile-cards'>
            {entries.map((entry) => (
              <div
                key={entry._id}
                className='kb-card'
                onClick={() =>
                  navigate(`/admin/knowledge-base-entry/${entry._id}`)
                }
              >
                <div className='card-top'>
                  <span className={` ${entry.category}`}>
                    {CATEGORY_LABELS[entry.category] || entry.category}
                  </span>
                  <span
                    className={`kb-badge ${entry.isActive ? 'active' : 'inactive'}`}
                  >
                    {entry.isActive ? 'Active' : 'Inactive'}
                  </span>
                </div>
                <p>{entry.question}</p>
                <div className='card-meta'>
                  <span>Priority: {entry.priority}</span>
                  <span>👁 {entry.metadata?.viewCount ?? 0}</span>
                  <span>Score: {helpfulScore(entry)}</span>
                </div>
              </div>
            ))}
          </div>

          <div className='kb-pagination'>
            <Pagination
              currentPage={page}
              totalCount={totalDocs}
              pageSize={20}
              onPageChange={(newPage: number) => handlePageChange(newPage)}
            />
          </div>
        </>
      )}
    </div>
  )
}

export default KnowledgeBase
